import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Collapse, Color, Button, Link, Tag, TagType, Icon, Spinner } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, PageTitle, Heading, TextColor } from "capitalrx-components-2.0/lib/typography";
import { formatDate } from "capitalrx-utilities";
import { CommunicationStatus, DocumentGetResponse } from "capitalrx-types";

import { CaseSearchForm, DetailsList, DocumentUpload } from "@/components";
import { CaseSearchErrors } from "@/constants";
import { ThemeSelectorContext } from "@/context";
import { getPACaseAPI } from "@/api";
import { PACaseResponse } from "@/api/interfaces/PACase";
import { formatPhoneWithExtension, getThemeURL, snakeCaseToTitleCase } from "@/utils";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";

import styles from "./PACasePage.module.scss";


export const PACasePage: FC = () => {
  const navigate = useNavigate();
  const { primary_color } = useContext(ThemeSelectorContext);

  const [caseParams, setCaseParams] = useState<CaseSearchForm>();
  const [loading, setLoading] = useState<boolean>(true);
  const [paCase, setPACase] = useState<PACaseResponse>();
  const [uploadedDocumentsExpanded, setUploadedDocumentsExpanded] = useState<boolean>(false);
  const [outreachDocumentsExpanded, setOutreachDocumentsExpanded] = useState<boolean>(false);

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });
  const StackOrShelf = isCompressed ? Stack : Shelf;

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const npiMemberId = searchParams.get("prescriber_npi");
    const externalMemberID = searchParams.get("external_member_id");
    const caseId = searchParams.get("case_id") ? Number(searchParams.get("case_id")) : null;
    const dob = searchParams.get("member_date_of_birth");

    if (caseId && npiMemberId && dob && externalMemberID) {
      const caseGetPayload = {
        case_id: caseId,
        prescriber_npi: npiMemberId,
        external_member_id: externalMemberID,
        member_date_of_birth: dob,
      };
      setCaseParams(caseGetPayload);
      getPACase(caseGetPayload);
      setSearchParams({});
    } else {
      navigate(getThemeURL("HOME"));
    }
  }, []);

  const goToHome = useCallback(() => {
    // analytics log case exit
    logEvent(ANALYTIC_EVENTS.PA_CASE.exit);
    navigate(getThemeURL("HOME"));
  }, [navigate]);

  const getPACase = useCallback(
    async (data: CaseSearchForm) => {
      setLoading(true);

      try {
        // Find PA Case and process response
        const caseResponse = await getPACaseAPI(data);
        setPACase(caseResponse);
        setLoading(false);

        // analytics log case found
        logEvent(ANALYTIC_EVENTS.PA_CASE_SEARCH.found);

      } catch (error) {
        const params = {
          error: CaseSearchErrors.NOT_FOUND,
          case_id: data.case_id,
          prescriber_npi: data.prescriber_npi,
          external_member_id: data.external_member_id,
          member_date_of_birth: data.member_date_of_birth,
        };

        const encodedParams = new URLSearchParams();
        for (const key in params) {
          if (params[key]) {
            encodedParams.append(key, params[key]);
          }
        }
        // analytics log case not found
        logEvent(ANALYTIC_EVENTS.ERRORS.caseNotFound);

        navigate({
          pathname: getThemeURL("PA_SEARCH"),
          search: encodedParams.toString(),
        });
      }
    },
    [],
  );

  const handleDocumentUploaded = useCallback(
    (data: DocumentGetResponse) => {
      if (paCase && data) {
        // analytics log case document upload
        logEvent(ANALYTIC_EVENTS.PA_CASE.upload);

        const newDocument = {
          communications: [],
          document: {
            document_type: data.document_type,
            created_at: data.created_at,
            method: data.method,
            tenant_id: data.tenant_id,
            uuid: data.uuid,
            rn: data.rn,
            filename: data.filename,
            url_for_upload: data.url_for_upload.url,
          },
          result_type: "document",
          uuid: data.uuid,
        };

        if (paCase.documents?.length) {
          // Add new doc to uploaded docs list
          setPACase({
            ...paCase,
            documents: [...paCase.documents, newDocument],
          });
        } else {
          setPACase({
            ...paCase,
            documents: [newDocument],
          });
        }
      }
    },
    [paCase],
  );

  const uploadedDocuments = useMemo(() => {
    if (!paCase?.documents) {
      return [];
    }
    return paCase.documents?.filter((d) => ["document", "inbound_fax"].includes(d.result_type));
  }, [paCase?.documents]);

  const outreachDocuments = useMemo(() => {
    if (!paCase?.documents) {
      return [];
    }
    const communications = [];
    for (const letter of paCase.documents) {
      if (letter.result_type === "letter" && letter?.communications?.length) {
        for (const com of letter.communications) {
          if (com.communication?.status !== CommunicationStatus.Failed) {
            communications.push(com.communication);
          }
        }
      }
    }
    return communications;
  }, [paCase?.documents]);

  const caseStatus = useMemo(() => {
    if (!paCase?.status) {
      return undefined;
    }

    if (paCase.status === "open") {
      return "In Review";
    } else if (paCase.status === "closed") {
      return "Closed";
    } else if (paCase.status === "complete") {
      if (paCase.case_outcome?.outcome === "denied") {
        return "Denied";
      } else if (paCase.case_outcome?.outcome === "approved") {
        return "Approved";
      }
    }
  }, [paCase]);

  const CaseTag: FC = useCallback(() => {
    const statusTagTypeMap = new Map<string, TagType>([
      ["In Review", "system-alternative-high-contrast"],
      ["Denied", "error-high-contrast"],
      ["Approved", "success-high-contrast"],
    ]);
    const tagType = statusTagTypeMap.get(caseStatus) ?? "info-high-contrast";

    return <Tag type={tagType} testId="test__prior_authorization_case_page_case_tag">{caseStatus}</Tag>;
  }, [caseStatus]);

  const caseDetails = useMemo(() => {
    if (!paCase) {
      return {};
    }

    let drugName = "";
    if (paCase.drug_name) {
      drugName = paCase.drug_name;
      if (paCase.drug_dosage_form) {
        drugName += `, ${paCase.drug_dosage_form}`;
      }
      if (paCase.drug_dosage) {
        drugName += ` (${paCase.drug_dosage})`;
      }
      if (paCase.drug_quantity) {
        drugName += `, Quantity: ${paCase.drug_quantity}`;
      }
      if (paCase.drug_days_supply) {
        drugName += `, Days Supply: ${paCase.drug_days_supply}`;
      }
    }

    let prescriberName = `${paCase.requesting_provider_contact_card.first_name} ${paCase.requesting_provider_contact_card.last_name}`;
    if (paCase.requesting_provider_contact_card.credential) {
      prescriberName += `, ${paCase.requesting_provider_contact_card.credential}`;
    }

    return {
      "Drug": drugName,
      "Prescriber": prescriberName,
      "Prescriber NPI": paCase.prescriber_npi,
      ...(paCase.requesting_provider_contact_card.fax_number && {
        "Fax": formatPhoneWithExtension(paCase.requesting_provider_contact_card.fax_number),
      }),
      "Member ID": paCase.member_external_id,
      "Patient Date of Birth": formatDate(paCase.member_date_of_birth, "MM/DD/YYYY"),
    };
  }, [paCase]);

  const caseActions = useMemo(() => {
    if (!paCase) {
      return {};
    }

    const details = {
      "Decision Deadline": formatDate(paCase.due_at, "MM/DD/YYYY hh:mma", true),
    };
    if (paCase.queue === "outreach") {
      details["Action Required"] = "Submit additional information";
    }

    return details;
  }, [paCase]);

  const caseOutcomes = useMemo(() => {
    if (!paCase?.case_outcome) {
      return {};
    }
    const outcome = {
      "Decision Date": formatDate(paCase.case_outcome.updated_at, "MM/DD/YYYY hh:mma", true),
    };

    if (caseStatus === "Denied") {
      let denialType = paCase.case_outcome.denial_type;
      let denialReason = paCase.case_outcome.denial_reason;

      if (denialType === "missing_information") {
        denialReason = "The request is denied due to errors in the patient's demographic details, incorrect CPT/ICD codes, or inaccurate provider information";
      } else if (!denialReason) {
        denialReason = "";
      } else {
        denialReason = snakeCaseToTitleCase(denialReason);
      }

      if (denialType === "missing_information") {
        denialType = "Incomplete or Incorrect Information";
      } else if (!denialType) {
        denialType = "";
      } else {
        denialType = snakeCaseToTitleCase(denialType);
      }

      outcome["Denial Type"] = denialType;
      outcome["Denial Reason"] = denialReason;
    } else if (caseStatus === "Approved" && paCase.effective_date && paCase.expiration_date) {
      const effective = formatDate(paCase.effective_date, "MM/DD/YYYY", true);
      const expiration = formatDate(paCase.expiration_date, "MM/DD/YYYY", true);
      outcome["Active Dates"] = `${effective} - ${expiration}`;
    }

    return outcome;
  }, [paCase]);

  return (
    <div className={styles.PACaseContainer} data-testid="test__prior_authorization_case_container">
      {loading
        && <Stack className={styles.LoadingContainer} spacing="small">
          <Heading className={styles.LoadingText}>Finding your case...</Heading>
          <Spinner className={styles.LoadingSpinner} size="large" />
        </Stack>}

      {(!loading && paCase)
        && <Stack spacing="large">
          <Stack>
            <StackOrShelf>
              <Stack spacing="small">
                <PageTitle testId="test__prior_authorization_case_page_case_id">Case ID {paCase.case_id}</PageTitle>
                <Shelf>
                  <CaseTag />
                  {paCase.is_urgent
                    && (
                      <Shelf spacing="mini">
                        <Icon name="warning-circle" color={Color.Red[600]} />
                        <TextColor appearance={Color.Red[600]} testId="test__prior_authorization_case_page_case_urgent">Urgent</TextColor>
                        <Shelf.Spacer />
                      </Shelf>
                    )
                  }
                  <Shelf.Spacer />
                </Shelf>
              </Stack>
              <Shelf.Spacer />
              <Button appearance="secondary" onClick={goToHome} testId="test__prior_authorization_case_page_exit_case">Exit Case</Button>
            </StackOrShelf>
            {caseStatus === "Denied"
              && <Body>To appeal this case, <Link style={{ color: primary_color }} to={getThemeURL("PA_CREATE")}>start a new case</Link> and select the case type "Appeal"</Body>
            }
          </Stack>
          <Stack>
            <Heading testId="test__prior_authorization_case_page_information_heading">Information</Heading>
            <DetailsList testId="test__prior_authorization_case_page_case_details" data={caseDetails} />
          </Stack>
          {caseStatus === "In Review"
            && <Stack>
              <Heading testId="test__prior_authorization_case_page_actions_heading">Actions</Heading>
              <DetailsList data={caseActions} testId="test__prior_authorization_case_page_case_details_list"/>
            </Stack>
          }
          {["Approved", "Denied"].includes(caseStatus)
            && <Stack>
              <Heading testId="test__prior_authorization_case_page_outcome_heading">Outcome</Heading>
              <DetailsList data={caseOutcomes} testId="test__prior_authorization_case_page_outcome_details_list"/>
            </Stack>
          }
          {(uploadedDocuments.length > 0 || outreachDocuments.length > 0 || caseStatus === "In Review")
            && <Stack>
              <Heading testId="test__prior_authorization_case_page_documents_heading">Documents</Heading>
              {caseStatus === "In Review"
                && (
                  <DocumentUpload
                    caseParams={caseParams}
                    handleUploadSuccess={handleDocumentUploaded}
                    data-testid="test__prior_authorization_case_page_documents_upload"
                  />
                )
              }

              {uploadedDocuments.length > 0
                && <Collapse
                  title="Uploaded Documents"
                  alignCaret="left"
                  expanded={uploadedDocumentsExpanded}
                  onToggleExpand={() => setUploadedDocumentsExpanded((prev) => !prev)}
                  keepHiddenContentInDOM={true}
                  testId="test__prior_authorization_case_page_uploaded_documents_collapse"
                >
                  <Stack testId="test__prior_authorization_case_page_uploaded_documents_info">
                    {uploadedDocuments.map((d) => {
                      if (d.document) {
                        return (
                          <StackOrShelf key={d.document.uuid} spacing="small" testId={`test__prior_authorization_case_page_uploaded_document_info_${d.document.uuid}`}>
                            <Shelf spacing="small">
                              <Icon name="document" />
                              <Body>{snakeCaseToTitleCase(d.document.document_type)}</Body>
                              <Shelf.Spacer />
                            </Shelf>
                            <Shelf.Spacer />
                            <Body>{formatDate(d.document.created_at, "MM/DD/YYYY hh:mm:ss A", true, true)}</Body>
                          </StackOrShelf>
                        );
                      } else if (d.inbound_fax) {
                        return (
                          <StackOrShelf key={d.inbound_fax.uuid} spacing="small" testId={`test__prior_authorization_case_page_uploaded_fax_info_${d.inbound_fax.uuid}`}>
                            <Shelf spacing="small">
                              <Icon name="document" />
                              <Body>{snakeCaseToTitleCase(d.inbound_fax.document_type)}</Body>
                              <Shelf.Spacer />
                            </Shelf>
                            <Shelf.Spacer />
                            <Body>{formatDate(d.inbound_fax.created_at, "MM/DD/YYYY hh:mm:ss A", true, true)}</Body>
                          </StackOrShelf>
                        );
                      }
                    })}
                  </Stack>
                </Collapse>
              }

              {outreachDocuments.length > 0
                && <Collapse
                  title="Outreach Documents"
                  alignCaret="left"
                  expanded={outreachDocumentsExpanded}
                  onToggleExpand={() => setOutreachDocumentsExpanded((prev) => !prev)}
                  keepHiddenContentInDOM={true}
                  testId="test__prior_authorization_case_page_outreach_documents_collapse"
                >
                  <Stack testId="test__prior_authorization_case_page_outreach_documents_info">
                    {outreachDocuments.map((d) => {
                      return (
                        <StackOrShelf key={d.id} spacing="small" testId={`test__prior_authorization_case_page_outreach_document_info_${d.id}`}>
                          <Shelf spacing="small">
                            <Icon name="document" />
                            <Body>{snakeCaseToTitleCase(d.communication_type)}</Body>
                            <Shelf.Spacer />
                          </Shelf>
                          <Shelf.Spacer />
                          <Body>{formatDate(d.created_at, "MM/DD/YYYY hh:mm:ss A", true, true)}</Body>
                        </StackOrShelf>
                      );
                    })}
                  </Stack>
                </Collapse>
              }

            </Stack>
          }

        </Stack>
      }
    </div >
  );
};

export default PACasePage;
